import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { TeamBillingInfo } from '@overlayqa/shared';
import { useAuth } from '../lib/auth';
import { pollBillingUntilActive } from '../lib/pollBillingUntilActive';
import { apiBaseUrl } from '../lib/apiBaseUrl';
import { log } from '@/lib/logger';
import { useAppSession } from './useAppSession';

/**
 * Returns a function that force-refreshes billing after a purchase.
 *
 * Call it from Clerk's <CheckoutButton onSubscriptionComplete>. It hits the
 * cache-bypassing POST /api/billing/refresh (which re-reads Clerk directly,
 * independent of the async subscription webhook) and polls until billing confirms
 * a subscription rather than trial access. Each result updates the current
 * team's billing query so TrialNudgeBanner and BillingPage reflect the response.
 * Resolves with the last read (null when there is no team or no read landed),
 * so the caller can tell a confirmed plan from a poll that ran out.
 *
 * Without this the dashboard relied entirely on the Clerk webhook clearing the
 * server's 5-min plan cache plus a 60s client staleTime — a window that never
 * closes on localhost (no webhook) and lags on prod.
 */
export function useBillingRefresh(): () => Promise<TeamBillingInfo | null> {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const { data: session } = useAppSession();
  const teamId = session?.team?.id;
  const userId = session?.user?.id;

  return useCallback(async () => {
    if (!teamId) return null;
    const fetchFresh = async (): Promise<TeamBillingInfo> => {
      const token = await getToken();
      const res = await fetch(`${apiBaseUrl()}/api/billing/refresh?teamId=${encodeURIComponent(teamId)}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Billing refresh failed (${res.status})`);
      const json = await res.json();
      return json.data as TeamBillingInfo;
    };

    log.info('[useBillingRefresh] refreshing after checkout');
    return pollBillingUntilActive({
      fetchFresh,
      onUpdate: (info) => queryClient.setQueryData(['billing', 'plan', teamId, userId], info),
    });
  }, [getToken, queryClient, teamId, userId]);
}
